// The ONE place the personal shell decides "which menu does this user see".
//
// Before 2026-10-02 the acting-role → NavContext mapping was copied verbatim
// into the desktop sidebar and the mobile sheet, the bottom tab bar kept a
// third, hand-written list with no role input, and `/home` read the raw
// acting-role cookie. Each surface re-decided navigation on its own, and they
// drifted (navigation audit 2026-10-02, F7/F11/F21). Every surface now calls
// `resolveShellNav` and only renders what it returns:
//
//   - desktop sidebar + mobile More sheet → `sections`
//   - mobile tab bar                      → `primaryTabs`
//   - "Acting as" switchers (desktop, mobile sheet, settings/roles)
//                                         → `actingRoleOptions`
//   - `/home` overrides                   → `actingRole`
//
// Pure module (no JSX, no server-only imports) so server components, client
// components and tests all share it.

import {
  adminNavItems,
  type AdminNavItem,
} from '~/home/(user)/_components/admin-nav-data';
import type { Brand } from '~/lib/brand';
import {
  isActingSuperAdmin,
  resolveActingRole,
  type ActingRoleContext,
} from './acting-role';
import type {
  NavContext,
  NavItemDef,
  NavSection,
  PartnerCompetitionRef,
} from './nav-types';
import { resolveSections } from './resolve-roles';

/** Bottom-bar slots before "More" (5 columns in total). */
export const MAX_PRIMARY_TABS = 4;

/** The workspace fields the shell navigation depends on. `UserWorkspace`
 *  satisfies it structurally; tests pass plain objects. */
export interface ShellNavWorkspace extends ActingRoleContext {
  isClientUser: boolean;
  isDancer: boolean;
  isOrganizer: boolean;
  isSeller: boolean;
  hasPerformanceData: boolean;
  /** Reimbursement module on for ≥1 of the dancer's clients; absent = on. */
  hasReimbursements?: boolean;
  /** Dancer-invoicing module on for ≥1 of the dancer's clients; absent = on. */
  hasDancerInvoicing?: boolean;
  needsProfileSetup: boolean;
  hasPendingOrgJoinRequest?: boolean;
  clientRole: 'owner' | 'member' | null;
  partnerCompetitions: PartnerCompetitionRef[];
  dancerProfileSlug: string | null;
}

/** One entry of the "Acting as" switcher. */
export interface ActingRoleOption {
  value: string;
  labelKey: string;
}

/** A mobile tab: a leaf of the acting role's own menu. `label` is an i18n key
 *  (matrix leaves) or a plain string (the untranslated admin console). */
export interface PrimaryTab {
  path: string;
  label: string;
  icon: NavItemDef['icon'];
  end?: boolean;
}

export interface ShellNav {
  actingRole: string;
  /** Acting as Admin (super admin whose acting role is `superadmin`). */
  isActingSuperAdmin: boolean;
  /** The NavContext the matrix was resolved with (for schema/config callers). */
  navContext: NavContext;
  sections: NavSection[];
  primaryTabs: PrimaryTab[];
  actingRoleOptions: ActingRoleOption[];
}

/**
 * Flag-derived roles the "Acting as" switcher offers on top of the user's
 * professional_profiles rows. Super admins preview Admin and Dancer; client
 * members (and super admins, who preview the portal) get Client.
 * `competition_director` is intentionally not offered — the director concept
 * is being removed. `setActiveRoleAction` accepts exactly these tokens.
 */
export function buildActingRoleOptions(
  w: Pick<ShellNavWorkspace, 'isSuperAdmin' | 'isClientUser'>,
): ActingRoleOption[] {
  return [
    ...(w.isSuperAdmin
      ? [
          {
            value: 'superadmin',
            labelKey: 'account:roleSwitcher.roleLabels.superAdmin',
          },
          {
            value: 'dancer',
            labelKey: 'onboarding:welcome.rolePicker.additionalRoles.dancer',
          },
        ]
      : []),
    ...(w.isClientUser
      ? [
          {
            value: 'client',
            labelKey: 'account:roleSwitcher.roleLabels.client',
          },
        ]
      : []),
  ];
}

/**
 * Map the acting role to the matrix context. The menu follows the ACTING
 * role, not the held roles (BAL-340):
 *  - Admin  → admin-only sections (every user-surface modifier dropped);
 *  - Client → only the client portal (the portal IS the menu);
 *  - a super admin previewing Dancer → a clean dancer menu;
 *  - anyone else → their own composed menu, never the client portal (it is
 *    its own acting role).
 */
function toActingNavContext(
  w: ShellNavWorkspace,
  brand: Brand,
  actingRole: string,
  actingSuperAdmin: boolean,
): NavContext {
  const bare: NavContext = {
    isSuperAdmin: false,
    isClientUser: false,
    isDancer: false,
    isOrganizer: false,
    isSeller: false,
    hasPerformanceData: false,
    needsProfileSetup: false,
    hasPendingOrgJoinRequest: false,
    clientRole: null,
    brand,
    partnerCompetitions: [],
    dancerProfileSlug: null,
  };

  if (actingSuperAdmin) {
    return { ...bare, isSuperAdmin: true };
  }

  if (actingRole === 'client') {
    return { ...bare, isClientUser: true, clientRole: w.clientRole };
  }

  // The dancer PREVIEW must gate on the REAL super-admin flag:
  // resolveActingRole returns a regular dancer's own 'dancer' role, so without
  // this gate every dancer would lose their Seller/Performance/partner nav.
  if (w.isSuperAdmin && actingRole === 'dancer') {
    return {
      ...bare,
      isDancer: true,
      dancerProfileSlug: w.dancerProfileSlug,
    };
  }

  return {
    ...bare,
    isDancer: w.isDancer,
    isOrganizer: w.isOrganizer,
    isSeller: w.isSeller,
    hasPerformanceData: w.hasPerformanceData,
    hasReimbursements: w.hasReimbursements,
    hasDancerInvoicing: w.hasDancerInvoicing,
    needsProfileSetup: w.needsProfileSetup,
    // BAL-668: the "membership request pending" notice for a role-less
    // partner/staff awaiting org-admin approval.
    hasPendingOrgJoinRequest: w.hasPendingOrgJoinRequest ?? false,
    clientRole: w.clientRole,
    partnerCompetitions: w.partnerCompetitions,
    dancerProfileSlug: w.dancerProfileSlug,
  };
}

interface RankedLeaf {
  rank: number;
  order: number;
  tab: PrimaryTab;
}

/**
 * The tab bar is derived, never listed: every ranked leaf of the resolved
 * menu (plus the ranked admin items when the admin section is present),
 * lowest rank first, capped at {@link MAX_PRIMARY_TABS}. A tab can therefore
 * never point somewhere the acting role's own menu does not.
 */
export function derivePrimaryTabs(
  sections: NavSection[],
  adminItems: AdminNavItem[] = adminNavItems,
): PrimaryTab[] {
  const leaves: RankedLeaf[] = [];
  let order = 0;

  for (const section of sections) {
    if (section.kind === 'group') {
      for (const item of section.items) {
        if (item.primary === undefined) continue;
        leaves.push({
          rank: item.primary,
          order: order++,
          tab: {
            path: item.path,
            label: item.i18nKey,
            icon: item.icon,
            ...(item.end !== undefined ? { end: item.end } : {}),
          },
        });
      }
    } else if (section.kind === 'adminTools') {
      for (const item of adminItems) {
        if (item.primary === undefined) continue;
        leaves.push({
          rank: item.primary,
          order: order++,
          tab: {
            path: item.path,
            label: item.label,
            icon: item.icon,
            ...(item.end !== undefined ? { end: item.end } : {}),
          },
        });
      }
    }
  }

  leaves.sort((a, b) => a.rank - b.rank || a.order - b.order);

  const seen = new Set<string>();
  const tabs: PrimaryTab[] = [];
  for (const leaf of leaves) {
    if (seen.has(leaf.tab.path)) continue;
    seen.add(leaf.tab.path);
    tabs.push(leaf.tab);
    if (tabs.length === MAX_PRIMARY_TABS) break;
  }

  return tabs;
}

export function resolveShellNav(w: ShellNavWorkspace, brand: Brand): ShellNav {
  const actingRole = resolveActingRole(w);
  const actingSuperAdmin = isActingSuperAdmin(w);
  const navContext = toActingNavContext(w, brand, actingRole, actingSuperAdmin);

  // Acting as Admin, the admin section IS the menu — open it.
  const sections = resolveSections(navContext).map((section) =>
    section.kind === 'adminTools' && actingSuperAdmin
      ? { ...section, defaultOpen: true }
      : section,
  );

  return {
    actingRole,
    isActingSuperAdmin: actingSuperAdmin,
    navContext,
    sections,
    primaryTabs: derivePrimaryTabs(sections),
    actingRoleOptions: buildActingRoleOptions(w),
  };
}
